// Search palette — global ⌘K / Ctrl+K command palette for notes.
//
// Server-side search hits (title + body via the RAG index when
// available, falls back to substring) plus a synchronous title-match
// pass against the already-loaded flat note list, so even an offline
// reload of search-with-no-typing shows recent notes immediately.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type NotesSearchHit } from "../../lib/rpc";
import type { FlatNote } from "../../lib/use-notes";

interface PaletteProps {
  open: boolean;
  rootId: string | null;
  notes: FlatNote[];
  onClose: () => void;
  onPick: (path: string) => void;
}

export function NotesSearchPalette({ open, rootId, notes, onClose, onPick }: PaletteProps) {
  const [query, setQuery] = useState("");
  const [serverHits, setServerHits] = useState<NotesSearchHit[]>([]);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when the palette opens — stale query / selected
  // would be confusing on a re-open from a different note.
  useEffect(() => {
    if (open) {
      setQuery("");
      setServerHits([]);
      setSelected(0);
      // requestAnimationFrame so the input is mounted before focus.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Debounced server search.
  useEffect(() => {
    if (!open || !rootId) return;
    const q = query.trim();
    if (!q) { setServerHits([]); return; }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const hits = await api.notesSearch(rootId, q, 25);
        if (!cancelled) setServerHits(hits);
      } catch {
        if (!cancelled) setServerHits([]);
      }
    }, 180);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [query, rootId, open]);

  // Merge: title-match notes first (instant, no network), then any
  // server hits not already covered. Dedupe by path.
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const seen = new Set<string>();
    const out: Array<{ path: string; title: string; snippet?: string }> = [];
    if (!q) {
      // Empty query: show the 12 most recent notes for quick jumps.
      for (const n of notes.slice(0, 12)) {
        if (seen.has(n.path)) continue;
        seen.add(n.path);
        out.push({ path: n.path, title: n.title });
      }
      return out;
    }
    for (const n of notes) {
      if (n.title.toLowerCase().includes(q)) {
        if (seen.has(n.path)) continue;
        seen.add(n.path);
        out.push({ path: n.path, title: n.title });
      }
    }
    for (const h of serverHits) {
      if (seen.has(h.path)) continue;
      seen.add(h.path);
      // Pull a friendly title from the path's last segment when the
      // server doesn't return one (it usually doesn't — hits are
      // path + snippet).
      const base = h.path.split("/").pop()?.replace(/\.[^.]+$/, "") || h.path;
      out.push({ path: h.path, title: base, snippet: h.snippet });
    }
    return out;
  }, [query, notes, serverHits]);

  // Keyboard handling — arrow keys cycle through items, Enter opens.
  // Escape closes. The outer onKeyDown captures these before they bubble
  // to the document-level shortcut listener in the parent.
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setSelected((s) => Math.min(items.length - 1, s + 1)); return; }
    if (e.key === "ArrowUp")   { e.preventDefault(); setSelected((s) => Math.max(0, s - 1)); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = items[selected];
      if (pick) { onPick(pick.path); onClose(); }
    }
  };

  // Clamp selected when the list shrinks underneath it.
  useEffect(() => {
    if (selected >= items.length) setSelected(Math.max(0, items.length - 1));
  }, [items.length, selected]);

  if (!open) return null;
  return (
    <div className="notes-palette-backdrop" onClick={onClose}>
      <div className="notes-palette" role="dialog" aria-label="Search notes" onClick={(e) => e.stopPropagation()}>
        <div className="notes-palette-input">
          <Icon name="search" size={14} />
          <input
            ref={inputRef}
            placeholder="Search notes by title or content…"
            value={query}
            onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
            onKeyDown={onKeyDown}
          />
          <kbd className="notes-palette-hint">esc</kbd>
        </div>
        <div className="notes-palette-list">
          {items.length === 0 ? (
            <div className="notes-palette-empty">No matches.</div>
          ) : items.map((it, i) => (
            <button
              key={it.path}
              className="notes-palette-row"
              data-active={i === selected}
              onMouseEnter={() => setSelected(i)}
              onClick={() => { onPick(it.path); onClose(); }}
              type="button"
            >
              <div className="notes-palette-row-title">{it.title}</div>
              {it.snippet && (
                <div className="notes-palette-row-snippet">{it.snippet}</div>
              )}
              <div className="notes-palette-row-path">{it.path}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
